import { supabase, getSupabaseUrl } from '../lib/supabase';
import { Property, UserProfile, SavedReport, SystemUser } from '../types';
import { MarketplaceAPI } from '../lib/marketplace';
import { adaptCanonicalToProperty } from '../lib/canonical-adapter';
import { calculateROI } from '../lib/roi-calculator';
import { invokeEdge } from '../lib/edge';
import { AI_FEATURES_ENABLED, ANALYTICS_FEATURES_ENABLED, safeLog } from '../lib/featureFlags';
import { normalizeLocationsOnLoad, validateAndNormalizeLocationsForSave } from '../lib/preference-normalizers';
import { isUuid } from '../lib/isUuid';
import { loadGuestShortlist, toggleGuestShortlist } from '../lib/guestShortlist';


export const api = {
  // --- PROPERTIES ---
  properties: {
    list: async (page = 1, filters = {}): Promise<{ properties: Property[], total: number }> => {
      // 1. Fetch from Reelly Proxy with Pagination
      try {
          // Pass page and filters to MarketplaceAPI
          const reellyData = await MarketplaceAPI.getProjects({ 
              page: page, 
              limit: 24,
              ...filters
          });
          
          if (reellyData && reellyData.items) {
              // reellyData.items are already CanonicalProject
              const adapted = reellyData.items.map(adaptCanonicalToProperty);
              return { 
                  properties: adapted, 
                  total: reellyData.total 
              };
          }
      } catch (e) {
          console.error("Reelly API Failed", e);
      }

      // Fallback (Empty) - avoiding local DB fallback mixing to prevent confusion
      return { properties: [], total: 0 };
    },

    get: async (id: string | number): Promise<Property | null> => {
      // Always try Reelly first - IDs can be numeric (1, 2, 3) or strings
      try {
         const cp = await MarketplaceAPI.getProjectDetails(String(id));
         if (cp) {
             const property = adaptCanonicalToProperty(cp);

             // Try to fetch AI Analytics from Supabase (AI ROI) - Only if enabled
             if (ANALYTICS_FEATURES_ENABLED || AI_FEATURES_ENABLED) {
                 try {
                    // Use maybeSingle() instead of single() to avoid 406 errors when no row exists
                    const { data: analytics, error: analyticsError } = await supabase
                        .from('project_analytics')
                        .select('*')
                        .eq('reelly_id', cp.id)
                        .maybeSingle();
                    
                    // Handle 406 or other errors gracefully
                    if (analyticsError) {
                        // 406 = Not Acceptable (no rows) - this is expected, not an error
                        if (analyticsError.code !== 'PGRST116' && analyticsError.code !== '406') {
                            safeLog.warn('[API] Analytics fetch error (non-critical):', analyticsError.code);
                        }
                        // Continue without analytics - not a blocking error
                    } else if (analytics) {
                        property.aiAnalytics = {
                            roi: analytics.ai_roi,
                            text: analytics.analysis_text,
                            confidence: analytics.confidence_score
                        };
                        // Override ROI with AI version
                        property.roi = `+${analytics.ai_roi}%`;
                    } else if (AI_FEATURES_ENABLED) {
                        // Trigger Cloud Function to analyze in background (fire & forget)
                        // Only if AI features are enabled
                        fetch(getSupabaseUrl('functions/v1/analyze-roi'), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                            },
                            body: JSON.stringify({
                                reelly_id: cp.id,
                                project_data: {
                                    name: cp.title,
                                    developer: cp.developer,
                                    location: cp.location.district,
                                    price: property.price,
                                    sqft: property.sqft,
                                    features: property.features
                                }
                            })
                        }).catch(err => {
                            // Fail silently - this is a background operation
                            safeLog.warn('[API] Background AI analysis failed (non-critical):', err.message || 'Unknown error');
                        });
                    }
                 } catch (e: any) {
                     // Catch any unexpected errors and fail silently
                     safeLog.warn('[API] Analytics fetch exception (non-critical):', e?.message || 'Unknown error');
                 }
             }

             return property;
         }
      } catch (e) {
         console.log("Reelly Detail Fetch Failed", e);
      }

      // NO DB Fallback for Details if Reelly fails - we want to fail fast or show Not Found
      return null;
    },

    create: async (property: Omit<Property, 'id'>): Promise<Property> => {
      // Reelly Read-Only
      throw new Error("Cannot create properties in Reelly mode.");
    },

    update: async (property: Property): Promise<Property> => {
       throw new Error("Cannot edit Reelly properties locally.");
    },

    delete: async (id: string | number): Promise<void> => {
       throw new Error("Cannot delete Reelly properties locally.");
    }
  },

  // --- AI SERVICES ---
  ai: {
    matchProperties: async (profile: any, properties: Property[], limit = 50): Promise<any> => {
      // Only call AI if features are enabled
      if (!AI_FEATURES_ENABLED) {
        safeLog.warn('[API] AI matchProperties called but AI features are disabled');
        // Return empty/null result - UI should handle this gracefully
        return null;
      }
      
      try {
        return await invokeEdge('analyze-market', {
          action: 'match_properties',
          context: {
            profile,
            // Optimization: Send reduced payload
            properties: properties.slice(0, limit).map(p => ({
              id: p.id,
              title: p.title,
              location: p.location,
              price: p.price,
              type: p.type,
              roi: p.roi,
              completion: p.completion
            }))
          }
        });
      } catch (e: any) {
        // Fail gracefully - return null instead of throwing
        safeLog.warn('[API] AI matchProperties failed (non-critical):', e?.message || 'Unknown error');
        return null;
      }
    }
  },

  // --- USER DATA ---
  user: {
    getProfile: async (userId: string): Promise<UserProfile | null> => {
      // CRITICAL FIX: Use .maybeSingle() instead of .single() to handle cases where
      // multiple rows might exist (shouldn't happen after migration, but defensive coding)
      // If multiple rows exist, we'll take the first one and log a warning
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      // Handle error cases
      if (error) {
        // PGRST116 = "no rows returned" - this is fine, user doesn't have a profile yet
        if (error.code === 'PGRST116') {
          return null;
        }
        // PGRST116 is the only "not found" error we should ignore
        // Any other error should be thrown
        throw error;
      }
      
      if (!data) return null;

      // Manual Mapping: DB (snake_case) -> App (camelCase)
      // CRITICAL: Normalize preferred locations on load to handle legacy values
      return {
        name: data.name,
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        phone: data.phone,
        avatar: data.avatar,

        // Derived from data presence
        isConfigured: !!data.buyer_type, 

        buyerType: data.buyer_type,
        budgetRange: data.budget_range,
        preferredLocations: normalizeLocationsOnLoad(data.preferred_locations), // Normalize on load
        propertyTypes: data.property_types || [],
        investmentGoals: data.investment_goals || [],
        
        aiSummary: data.ai_summary,
        lastUpdated: data.last_updated,

        // Notification Preferences - use actual DB values, not defaults
        emailNotifications: {
          newProperties: data.email_notifications_new_properties !== null && data.email_notifications_new_properties !== undefined 
            ? data.email_notifications_new_properties 
            : true,
          priceDrops: data.email_notifications_price_drops !== null && data.email_notifications_price_drops !== undefined 
            ? data.email_notifications_price_drops 
            : true,
          marketUpdates: data.email_notifications_market_updates !== null && data.email_notifications_market_updates !== undefined 
            ? data.email_notifications_market_updates 
            : true,
        },
        smsNotifications: {
          urgentDeals: data.sms_notifications_urgent_deals !== null && data.sms_notifications_urgent_deals !== undefined 
            ? data.sms_notifications_urgent_deals 
            : false,
          callReminders: data.sms_notifications_call_reminders !== null && data.sms_notifications_call_reminders !== undefined 
            ? data.sms_notifications_call_reminders 
            : true,
        },
        notificationFrequency: (data.notification_frequency as 'real-time' | 'daily' | 'weekly') || 'real-time'
      };
    },

    updateProfile: async (userId: string, profile: UserProfile): Promise<void> => {
      // Manual Mapping: App (camelCase) -> DB (snake_case)
      // CRITICAL: Normalize preferred locations before saving to ensure only emirates are stored
      const dbProfile = {
        id: userId, // CRITICAL: Explicitly set id for upsert conflict resolution
        name: profile.name,
        first_name: profile.firstName,
        last_name: profile.lastName,
        email: profile.email,
        phone: profile.phone,
        avatar: profile.avatar,
        
        buyer_type: profile.buyerType,
        budget_range: profile.budgetRange,
        preferred_locations: validateAndNormalizeLocationsForSave(profile.preferredLocations || []), // Normalize before save
        property_types: profile.propertyTypes || [], // Ensure array, not null
        investment_goals: profile.investmentGoals || [], // Ensure array, not null
        
        ai_summary: profile.aiSummary,
        last_updated: new Date().toISOString(),

        // Notification Preferences
        email_notifications_new_properties: profile.emailNotifications?.newProperties ?? true,
        email_notifications_price_drops: profile.emailNotifications?.priceDrops ?? true,
        email_notifications_market_updates: profile.emailNotifications?.marketUpdates ?? true,
        sms_notifications_urgent_deals: profile.smsNotifications?.urgentDeals ?? false,
        sms_notifications_call_reminders: profile.smsNotifications?.callReminders ?? true,
        notification_frequency: profile.notificationFrequency || 'real-time'
      };

      // CRITICAL FIX: Use upsert with explicit conflict target on 'id'
      // This ensures:
      // 1. If profile exists (id matches), it updates that row
      // 2. If profile doesn't exist, it inserts a new row
      // 3. Prevents duplicate creation by using id as the conflict key
      // 4. Returns the single row result to avoid "cannot coerce" error
      const { data, error } = await supabase
        .from('profiles')
        .upsert(dbProfile, {
          onConflict: 'id', // Explicitly specify conflict resolution on id column
          ignoreDuplicates: false // We want to update if exists, not ignore
        })
        .select()
        .single(); // Ensure we get exactly one row back
        
      if (error) {
        // Log error details for debugging (dev only)
        if (import.meta.env.DEV) {
          console.error('[api.updateProfile] Upsert failed:', {
            userId,
            errorCode: error.code,
            errorMessage: error.message,
            errorDetails: error.details,
            errorHint: error.hint
          });
        }
        throw error;
      }
      
      // Verify we got a result (defensive check)
      if (!data) {
        throw new Error('Profile upsert succeeded but no data returned');
      }
    },

    getShortlist: async (userId: string | undefined): Promise<string[]> => {
       // GUARD: If userId is missing or not a UUID, use guest shortlist (localStorage)
       if (!userId || !isUuid(userId)) {
         if (import.meta.env.DEV) {
           console.log('[api.user.getShortlist] Guest mode - loading from localStorage');
         }
         const guestList = loadGuestShortlist();
         return guestList.map(id => String(id));
       }

       // Graceful fail if table blocked
       try {
           if (import.meta.env.DEV) {
             console.log('[api.user.getShortlist] Fetching shortlist for user:', userId);
           }
           
           const { data, error } = await supabase
            .from('shortlists')
            .select('property_id')
            .eq('user_id', userId);
            
           if (error) {
             if (import.meta.env.DEV) {
               console.error('[api.user.getShortlist] Error:', error);
             }
             throw error;
           }
           
           const ids = (data || []).map(row => String(row.property_id));
           
           if (import.meta.env.DEV) {
             console.log('[api.user.getShortlist] Loaded', ids.length, 'items:', ids);
           }
           
           return ids;
       } catch (e: any) {
           if (import.meta.env.DEV) {
             console.error("[api.user.getShortlist] Shortlist fetch failed", e);
             // Check if it's an RLS/permission error
             if (e?.code === '42501' || e?.code === 'PGRST301' || 
                 (e?.message && (e.message.includes('JWT') || e.message.includes('permission') || e.message.includes('unauthorized')))) {
               console.warn('[api.user.getShortlist] RLS/permission error - user may not have access');
             }
           }
           return [];
       }
    },

    toggleShortlist: async (userId: string | undefined, propertyId: string | number): Promise<(string | number)[]> => {
       // Convert to string for consistency
       const propertyIdStr = String(propertyId);
       
       // GUARD: If userId is missing or not a UUID, use guest shortlist (localStorage)
       if (!userId || !isUuid(userId)) {
         if (import.meta.env.DEV) {
           console.log('[api.user.toggleShortlist] Guest mode - toggling in localStorage');
         }
         const current = loadGuestShortlist();
         const next = toggleGuestShortlist(current, propertyId);
         return next;
       }

       if (import.meta.env.DEV) {
         console.log('[api.user.toggleShortlist] Toggling shortlist:', { userId, propertyId: propertyIdStr });
       }

       // Check if exists
       const { data, error: checkError } = await supabase
         .from('shortlists')
         .select('*')
         .eq('user_id', userId)
         .eq('property_id', propertyIdStr)
         .maybeSingle(); // Use maybeSingle to avoid error if not found

       if (checkError && checkError.code !== 'PGRST116') {
         if (import.meta.env.DEV) {
           console.error('[api.user.toggleShortlist] Check error:', checkError);
         }
         throw checkError;
       }

       if (data) {
         // Remove from shortlist
         const { error: deleteError } = await supabase
           .from('shortlists')
           .delete()
           .eq('user_id', userId)
           .eq('property_id', propertyIdStr);
         
         if (deleteError) {
           if (import.meta.env.DEV) {
             console.error('[api.user.toggleShortlist] Delete error:', deleteError);
           }
           throw deleteError;
         }
         
         if (import.meta.env.DEV) {
           console.log('[api.user.toggleShortlist] Removed from shortlist');
         }
       } else {
         // Add to shortlist (use upsert to avoid duplicates)
         const { error: insertError } = await supabase
           .from('shortlists')
           .upsert({ 
             user_id: userId, 
             property_id: propertyIdStr 
           }, {
             onConflict: 'user_id,property_id'
           });
         
         if (insertError) {
           if (import.meta.env.DEV) {
             console.error('[api.user.toggleShortlist] Insert error:', insertError);
           }
           throw insertError;
         }
         
         if (import.meta.env.DEV) {
           console.log('[api.user.toggleShortlist] Added to shortlist');
         }
       }

       // Return updated shortlist for state sync
       const updated = await api.user.getShortlist(userId);
       return updated;
    },

    getReports: async (userId: string): Promise<SavedReport[]> => {
        try {
            const { data, error } = await supabase
                .from('saved_reports')
                .select('*')
                .eq('user_id', userId);
            
            if (error) {
                // Ignore table not found to prevent login crash
                if (error.code === '42P01') return [];
                throw error;
            }
            return data as unknown as SavedReport[];
        } catch (e) {
            console.error("Saved Reports fetch failed", e);
            return [];
        }
    },
    
    saveReport: async (userId: string, report: SavedReport): Promise<void> => {
        const { error } = await supabase
            .from('saved_reports')
            .insert({ 
                user_id: userId,
                title: report.title,
                created_at: report.date,
                data: report 
            });
            
        if (error) throw error;
    }
  }
};
